// useMenuState.ts — Sprint 14. Menu / sidebar state for the
// dashboard: which page is selected, which groups are expanded,
// the role+feature-filtered item list, and the three handlers
// that the sidebar passes back to us.
//
// Sprint R11-8 (ADR-128) — the selected page is now DERIVED FROM THE URL
// rather than held in component state. `/condo-app/<key>` is the page; the
// registry key IS the slug (they were already slug-shaped). Every navigation
// in the app funnels through `selectAndReveal`, so pointing that one function
// at `navigate()` makes the whole shell URL-driven at once: the address bar
// tracks the view, deep links work, and Back/Forward behave.
//
// Two consequences worth naming:
//   * The old role-default EFFECT (setSelected('my-home') once role resolved)
//     is gone. It raced the URL — a deep link to /condo-app/payments would be
//     stomped when `user` resolved asynchronously. The bare path now REDIRECTS
//     to the role's default instead, so a slug in the URL always wins.
//   * Unknown/inaccessible slugs are reported via `routeStatus` instead of
//     silently rendering the role home (the R11-8 defect).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ALL_MENU_ITEMS,
  passesFeatureGate,
  passesRoleGate,
  type DashboardComponent,
  type MenuGroupKey,
} from '../menuConfig';
import type { CondoUser } from '../../../../../context/UnifiedAuthContext';

/** The condo shell's URL prefix. The page key is appended as one segment. */
export const CONDO_BASE = '/condo-app';

/** Path for a page key. Single helper so the shape lives in one place. */
export const pathForKey = (key: DashboardComponent): string => `${CONDO_BASE}/${key}`;

/**
 * What the current URL resolves to:
 *   resolving  — bare `/condo-app/`; we're redirecting to the role default
 *                (or still waiting for `user` to resolve).
 *   ok         — a real page this user may see.
 *   forbidden  — a real page this user may NOT see (role/feature gated).
 *   not-found  — no such page.
 */
export type RouteStatus = 'resolving' | 'ok' | 'forbidden' | 'not-found';

export interface UseMenuStateArgs {
  user:            CondoUser | null;
  featureSettings: any;
}

export interface UseMenuState {
  selected:         DashboardComponent;
  setSelected:      (key: DashboardComponent) => void;
  expandedGroups:   Set<string>;
  toggleGroup:      (groupKey: MenuGroupKey) => void;
  filteredItems:    typeof ALL_MENU_ITEMS;
  /** Combines navigation with auto-expanding the item's group so
   *  the sidebar doesn't show a selected child under a collapsed
   *  parent. */
  selectAndReveal:  (key: DashboardComponent) => void;
  /** How the current URL resolved — drives 404/403 rendering. */
  routeStatus:      RouteStatus;
  /** The raw slug from the URL (''
   *  for the bare path). Exposed for the not-found message. */
  routeSlug:        string;
}

/** First path segment after `/condo-app/`, or '' for the bare path. */
const slugFromPathname = (pathname: string): string => {
  const m = pathname.match(/^\/condo-app\/?(.*)$/);
  return (m ? m[1] : '').split('/')[0] || '';
};

export function useMenuState({ user, featureSettings }: UseMenuStateArgs): UseMenuState {
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(['Overview', 'Property', 'Residents', 'Finances', 'Operations', 'System']),
  );

  const filteredItems = useMemo(
    () => ALL_MENU_ITEMS.filter(
      item => passesRoleGate(item.permission, user?.role)
           && passesFeatureGate(item.key, user?.role, featureSettings),
    ),
    [user?.role, featureSettings],
  );

  // The role's landing page. Prefer the role's natural home, but fall back to
  // the first page the user can actually reach — a feature-gated home would
  // otherwise strand them on a page they're not allowed to see.
  const roleDefaultKey: DashboardComponent | undefined = useMemo(() => {
    const preferred: DashboardComponent =
      user?.role === 'owner'        ? 'my-home'
      : user?.role === 'board-member' ? 'board-member-portal'
      : 'overview';
    if (filteredItems.some(i => i.key === preferred)) return preferred;
    return filteredItems[0]?.key;
  }, [user?.role, filteredItems]);

  const routeSlug = useMemo(() => slugFromPathname(location.pathname), [location.pathname]);

  // Resolve the slug against the registry + this user's gates.
  const { routeStatus, selected } = useMemo(() => {
    const known = ALL_MENU_ITEMS.find(i => i.key === routeSlug)?.key;
    const allowed = known != null && filteredItems.some(i => i.key === known);

    const status: RouteStatus =
      routeSlug === '' ? 'resolving'
      : known == null   ? 'not-found'
      : !allowed        ? 'forbidden'
      : 'ok';

    // `selected` drives nav highlighting + the page title. On a 404/403 there
    // is no legitimate selection, so fall back to the role default purely so
    // the chrome renders coherently behind the Result.
    return { routeStatus: status, selected: (allowed ? known : roleDefaultKey) ?? 'overview' };
  }, [routeSlug, filteredItems, roleDefaultKey]);

  // Bare `/condo-app/` → the role's default page. Waits for `user` to resolve,
  // so this can never stomp a slug the visitor actually asked for.
  useEffect(() => {
    if (routeSlug !== '') return;
    if (!user?.role || !roleDefaultKey) return;
    navigate(pathForKey(roleDefaultKey), { replace: true });
  }, [routeSlug, user?.role, roleDefaultKey, navigate]);

  const toggleGroup = (groupKey: MenuGroupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const ensureGroupExpanded = (itemKey: DashboardComponent) => {
    const item = ALL_MENU_ITEMS.find(i => i.key === itemKey);
    if (item) {
      setExpandedGroups(prev =>
        prev.has(item.group) ? prev : new Set(Array.from(prev).concat(item.group)),
      );
    }
  };

  // Every nav button, in-page CTA, and command-palette entry lands here.
  const selectAndReveal = useCallback((key: DashboardComponent) => {
    ensureGroupExpanded(key);
    navigate(pathForKey(key));
  }, [navigate]);

  return {
    selected,
    setSelected: selectAndReveal,
    expandedGroups, toggleGroup,
    filteredItems, selectAndReveal,
    routeStatus, routeSlug,
  };
}
